import { axios } from '@/axios';
import routes from '@/web-api/routes';
import storageManager from '@/storage-manager';
import storeHelper from '@/vuex/helper';

// TODO consistent return values for resolve/reject

export default {
  /**
   * Loads all the users shortlised jobs from the server and updates vuex and session storage.
   *
   * @return Promise - End point returns and array of all shortlisted items by the user.
   *
   * @param {*} store
   */
  getSavedItems({ commit }) {
    return new Promise((resolve, reject) => {
      axios.get(routes.webapi.user.savedItems.get)
        .then((response) => {
          commit('setItemData', response.data.data);
          return resolve(true);
        })
        .catch((error) => {
          storeHelper.handleUnauthorised(error);
          return reject(error);
        });
    });
  },

  /**
   * Adds a new item to the users saved items
   *
   * Scenario 1: save an item when logged in
   * Scenario 2: save an item when not logged in
   * Scenario 3: save an item when not logged but have saved it in the past
   *
   * @return Promise - Object of newly saved item
   *
   * @param {*} store
   * @param {*} stor
   * @param {*} payload
   */
  addItem({ commit, dispatch }, payload) {
    return new Promise((resolve, reject) => {
      axios.post(
        routes.webapi.user.savedItems.update + payload.type,
        { id: payload.id, reminders: payload.reminders },
      )
        .then((response) => {
          const { data } = response.data;
          commit('addItem', data);

          return resolve({ response });
        })
        .catch((error) => {
          storeHelper.handleUnauthorised(error);

          // A 422 happens when you try to shortlist an item already in your shortlists
          // this only really happens if you are shortlisting in multiple tabs, or theres
          // a bug elsewhere.
          // Here we resync the local items via ajax to try to resolve this.
          if (error.response.status === 422) {
            storageManager.emptySessionStorage();
            dispatch('getSavedItems');
          }

          return reject(error);
        });
    });
  },

  /**
   * Delete a saved item
   *
   * @return Promise - End point returns an object containing {id: 17484, type: 'jobs'}
   *
   * @param {*} store
   * @param {*} payload - {id: 17484, type: 'jobs'}
   */
  deleteItem({ commit }, payload) {
    return new Promise((resolve, reject) => {
      axios.delete(
        `${routes.webapi.user.savedItems.update + payload.type}/${payload.id}`,
      )
        .then((response) => {
          const { data } = response.data;
          commit('removeItem', data);
          return resolve(response);
        })
        .catch((error) => {
          storeHelper.handleUnauthorised(error);

          // TODO can we get a 422, from a delete or is it a 404?

          return reject(error);
        });
    });
  },

  /**
   * Updates the reminder boolean flag for a single company or job item
   *
   * Scenario 1: toggle individual item - { id: 123, type: 'jobs', reminders: 1||0 }
   *
   * @return Promise - End point returns the item that was updated.
   *
   * @param {*} store
   * @param {*} payload - { id: 123, type: 'jobs', reminders: 1||0 }
   */
  updateSingleReminder({ commit }, { type, id, reminders }) {
    return new Promise((resolve, reject) => {
      axios.put(
        `${routes.webapi.user.savedItems.update + type}/${id}`,
        { reminders },
      )
        .then((response) => {
          const { savedJobItems, savedCompanyItems } = response.data.data;

          if (type === 'jobs') {
            commit('updateItem', savedJobItems[0]);
          } else if (type === 'companies') {
            commit('updateItem', savedCompanyItems[0]);
          }
          return resolve(true);
        })
        .catch((error) => {
          storeHelper.handleUnauthorised(error);
          return reject(error);
        });
    });
  },

  /**
   * Updates the reminder boolean flag for all company or job items
   *
   * Scenario 1: toggle all - { type: 'jobs', reminders: 1||0 }
   *
   * @return Promise - End point returns and array of all saved items of that TYPE by the user.
   *
   * @param {*} store
   * @param {*} payload - { type: 'jobs', reminders: 1||0 }
   */
  updateAllRemindersOfType({ commit }, { type, reminders }) {
    return new Promise((resolve, reject) => {
      axios.put(
        `${routes.webapi.user.savedItems.update}${type}/all`,
        { reminders },
      )
        .then((response) => {
          const updatePayload = response.data.data.savedJobItems || response.data.data.savedCompanyItems;
          commit('replaceItemsByType', { items: updatePayload, type });
          return resolve(true);
        })
        .catch((error) => {
          storeHelper.handleUnauthorised(error);
          return reject(error);
        });
    });
  },
};
